import { parseGermanNumber } from '../../../utils/parseGermanNumber';
import type { Material } from '../types/material';
import { STAMMDATEN_LIST_PAGE_SIZE } from './listPageSize';

/** Vermeidet „Unexpected end of JSON input“, wenn der Server einen leeren Body liefert */
export async function parseApiJsonResponse(res: Response): Promise<unknown> {
  const text = await res.text();
  const trimmed = text.trim();
  if (!trimmed) {
    if (!res.ok) throw new Error(`Serverfehler (${res.status})`);
    throw new Error('Leere Serverantwort.');
  }
  try {
    return JSON.parse(trimmed) as unknown;
  } catch {
    throw new Error('Ungültige Serverantwort (kein JSON).');
  }
}

/**
 * Persist a row edit. Shared rows (supplier_materials) are saved via
 * /api/instance-material-settings/:id (overrides only); private rows go via
 * /api/materials/:id (full update).
 */
export async function saveMaterialEdit(
  m: Material,
  draft: Partial<Material>,
  options: { ekVal: number; surcharge: number; vatPercent: number }
): Promise<Material> {
  if (m.kind === 'shared') {
    const baseListPrice = Number(m.list_price ?? 0);
    const computedDefaultEk =
      m.discount_percentage != null && Number.isFinite(Number(m.discount_percentage))
        ? baseListPrice * (1 - Number(m.discount_percentage) / 100)
        : baseListPrice;
    const purchaseOverride =
      Number.isFinite(options.ekVal) && Math.abs(options.ekVal - computedDefaultEk) > 0.005
        ? options.ekVal
        : null;
    const surchargeOverride = Number.isFinite(options.surcharge) ? options.surcharge : null;
    const minutesOverride =
      draft.minutes !== undefined && draft.minutes !== null && Number.isFinite(Number(draft.minutes))
        ? Number(draft.minutes)
        : null;
    const body: Record<string, unknown> = {
      purchase_price_override: purchaseOverride,
      surcharge_percent_override: surchargeOverride,
      minutes_override: minutesOverride,
    };
    if (draft.folder_id !== undefined) body.folder_id = draft.folder_id;
    const res = await fetch(`/api/instance-material-settings/${m.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => null);
    if (!res.ok) throw new Error(data?.error || 'Material konnte nicht gespeichert werden.');
    const item = data?.item;
    if (!item) return m;
    return {
      ...m,
      ...item,
      kind: 'shared',
      id: String(item.supplier_material_id ?? m.id),
      net_price: item.effective_purchase_price ?? null,
      gross_price: item.effective_vk ?? null,
      surcharge_percent: item.effective_surcharge_percent ?? null,
      minutes: item.effective_minutes ?? null,
      list_price: item.list_price ?? null,
      supplier: item.supplier_name ?? m.supplier,
    } as Material;
  }
  const res = await fetch(`/api/materials/${m.id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: draft.name,
      brand: draft.brand ?? '',
      supplier: draft.supplier ?? '',
      gtin: draft.gtin ?? '',
      unit: draft.unit ?? '',
      net_price: options.ekVal,
      surcharge_percent: options.surcharge,
      vat_percent: options.vatPercent,
      description: draft.description ?? m.description ?? null,
      minutes: draft.minutes ?? null,
    }),
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error(data?.error || 'Material konnte nicht gespeichert werden.');
  return data as Material;
}

export function loadMaterialsFromApi(
  folderId: string | null,
  query: string,
  page: number,
  supplierId: string | null,
  preferredMode: 'semantic' | 'keyword',
  signal?: AbortSignal
): Promise<{
  rows: Material[];
  searchResultsCount: number;
  searchTotalPages: number;
  searchMode: 'semantic' | 'keyword' | 'exact' | null;
  searchPage: number;
  totalMaterialsCount?: number;
}> {
  const trimmedQuery = query.trim();
  if (trimmedQuery) {
    const params = new URLSearchParams({
      q: trimmedQuery,
      page: String(page),
      mode: preferredMode,
    });
    if (folderId) params.set('folder_id', folderId);
    if (supplierId) params.set('supplier_id', supplierId);
    return fetch(`/api/materials/search?${params.toString()}`, { signal })
      .then(async (r) => {
        const json = await r.json().catch(() => null);
        if (!r.ok) throw new Error(json?.error || `Suchfehler (${r.status})`);
        return json;
      })
      .then((data: { items?: Material[]; total?: number; total_pages?: number; mode?: 'semantic' | 'keyword' | 'exact'; page?: number }) => {
        const rows = Array.isArray(data?.items) ? data.items : [];
        return {
          rows,
          searchResultsCount: data?.total ?? rows.length,
          searchTotalPages: data?.total_pages ?? 1,
          searchMode: data?.mode ?? null,
          searchPage: data?.page ?? 1,
        };
      });
  }

  const params = new URLSearchParams();
  params.set('with_meta', '1');
  params.set('limit', String(STAMMDATEN_LIST_PAGE_SIZE));
  params.set('page', String(Math.max(1, page)));
  if (folderId) params.set('folder_id', folderId);
  if (supplierId) {
    params.set('supplier_id', supplierId);
    params.set('include_private', '0');
  }
  return fetch(`/api/materials?${params.toString()}`, { signal })
    .then((r) => (r.ok ? r.json() : { items: [], total_count: 0 }))
    .then((data) => {
      const payload =
        typeof data === 'object' && data
          ? (data as { items?: Material[]; total_count?: number; total_pages?: number; page?: number })
          : {};
      const rows = Array.isArray(payload.items) ? payload.items : [];
      const total = Number(payload.total_count);
      const safeTotal = Number.isFinite(total) && total >= 0 ? total : rows.length;
      const totalPagesRaw = Number(payload.total_pages);
      const totalPages =
        Number.isFinite(totalPagesRaw) && totalPagesRaw >= 1
          ? Math.trunc(totalPagesRaw)
          : Math.max(1, Math.ceil(safeTotal / STAMMDATEN_LIST_PAGE_SIZE) || 1);
      const pageRaw = Number(payload.page);
      const safePage = Number.isFinite(pageRaw) && pageRaw >= 1 ? Math.trunc(pageRaw) : Math.max(1, page);
      return {
        rows,
        searchResultsCount: safeTotal,
        searchTotalPages: totalPages,
        searchMode: null,
        searchPage: safePage,
        totalMaterialsCount: folderId === null && supplierId === null ? safeTotal : undefined,
      };
    });
}

export async function createMaterial(payload: {
  name: string;
  brand: string;
  supplier: string;
  gtin: string;
  unit: string;
  ekPrice: string;
  surchargePercent: string;
  vatPercent: string;
  description: string;
  minutes: string;
  folderId: string | null;
  ordnerFolderIds: Set<string>;
}): Promise<Material> {
  const sur = parseGermanNumber(payload.surchargePercent);
  const vatN = Number(payload.vatPercent);
  const folderPayload =
    payload.folderId && payload.ordnerFolderIds.has(payload.folderId)
      ? { folder_id: payload.folderId }
      : {};
  const minutesNum = payload.minutes.trim() !== '' ? parseInt(payload.minutes.trim(), 10) : null;
  const res = await fetch('/api/materials', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: payload.name,
      brand: payload.brand,
      supplier: payload.supplier,
      gtin: payload.gtin,
      unit: payload.unit,
      net_price: parseGermanNumber(payload.ekPrice),
      surcharge_percent: sur,
      vat_percent: vatN,
      description: payload.description.trim() || null,
      minutes: minutesNum != null && !isNaN(minutesNum) && minutesNum >= 0 ? minutesNum : null,
      ...folderPayload,
    }),
  });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((d as { error?: string }).error || 'Material konnte nicht angelegt werden.');
  return d as Material;
}

export async function deleteMaterial(id: string): Promise<void> {
  const res = await fetch(`/api/materials/${id}`, { method: 'DELETE' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error((data as { error?: string }).error || 'Material konnte nicht gelöscht werden.');
  }
}
